import React, { useCallback } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import clsx from 'clsx';
import { Conversation } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { Icon } from 'shared/components/Icon';
import { replaceConversation } from 'shared/store/conversation-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { clearChatMessages } from 'shared/store/chat-slice';
import { getVisitorDisplayName } from 'shared/utils/visitor';
import './style.less';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { parseMessage } from 'shared/utils/chat';

interface GlobalConversationHistoryItemProps {
  conversation: Conversation;
  openConversation: (visitorId: string, conversationId: number) => void;
}

const GlobalConversationHistoryItem: React.FC<GlobalConversationHistoryItemProps> = ({
  conversation,
  openConversation,
}) => {
  const dispatch = useAppDispatch();
  const visitorId = conversation.visitor.id;
  const activeConversation = useAppSelector(s => s.conversation.conversation);
  const isActive = conversation.id === activeConversation?.id;
  const user = useAppSelector(selectActiveUser);
  const members = useAppSelector(selectActiveOrgMembers);

  const isFromVisitor = conversation.lastMessage?.sourceType === 'visitor';
  const isFromAgent = conversation.lastMessage?.sourceType === 'agent';
  const isFromBot =
    conversation.lastMessage &&
    ['bot', 'system'].includes(conversation.lastMessage?.sourceType);

  const onClick = useCallback(async () => {
    await dispatch(clearChatMessages());
    await dispatch(replaceConversation({ conversationId: conversation.id }));
    openConversation(visitorId, conversation.id);
  }, [conversation.id, dispatch, openConversation, visitorId]);

  return (
    <button
      onClick={onClick}
      className={clsx(
        'GlobalConversationHistoryItem',
        isActive && 'GlobalConversationHistoryItem--active',
        !conversation.lastMessage?.dateRead && 'GlobalConversationHistoryItem--unread',
      )}
    >
      <div className="GlobalConversationHistoryItem__left">
        <div className="GlobalConversationHistoryItem__bubble">
          <Icon icon="bubble" />
        </div>
      </div>
      <div className="GlobalConversationHistoryItem__right">
        <div className="GlobalConversationHistoryItem__lastMessage">
          {parseMessage(conversation.lastMessage?.text ?? '', members, true)}
        </div>
        <div className="GlobalConversationHistoryItem__meta">
          {!!conversation.dateLastMessage &&
            formatDistanceToNowStrict(new Date(conversation.dateLastMessage), {
              addSuffix: true,
            })}
          &nbsp;&middot;&nbsp;
          {isFromVisitor && getVisitorDisplayName(conversation.visitor)}
          {isFromAgent && conversation.lastMessage?.agent.name}
          {isFromBot && user?.org.name}
        </div>
      </div>
    </button>
  );
};

export default GlobalConversationHistoryItem;
